import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, makeStyles } from '@material-ui/core';
import { Link } from 'react-router-dom';
import { useIndicadorApdex } from '../../../hooks/useIndicadorApdex';
import { AtCircularProgress } from '../../../components/AtCircularProgress';
import { getQueryParams } from '../../../utils/fns/get-query-params';
import SettingsIcon from '@material-ui/icons/Settings';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import ErrorIcon from '@material-ui/icons/Error';

const useStyles = makeStyles((theme) => ({
  header: {
    marginBottom: '20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
    flexWrap: 'wrap',
  },
  title: {
    fontWeight: 700,
    fontSize: '1.25rem',
    color: '#111827',
  },
  statusCircle: {
    position: 'relative',
    width: '180px',
    height: '180px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginRight: '24px'
  },
  statusIndicator: {
    textAlign: 'center',
  },
  summaryBox: {
    padding: '20px',
    borderRadius: '16px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    flex: 1,
    minWidth: '120px',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    }
  },
  placeholderContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: theme.spacing(4, 2),
    flex: 1,
    gap: theme.spacing(1.5),
  },
  configLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '0.8rem',
    fontWeight: 600,
    color: '#4F46E5',
    backgroundColor: '#EEF2FF',
    padding: '6px 12px',
    borderRadius: '20px',
    border: '1px solid #E0E7FF',
    textDecoration: 'none',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#E0E7FF',
      color: '#4338CA',
      textDecoration: 'none',
    },
  },
  stepName: {
    fontWeight: 700,
    fontSize: '1.1rem',
    color: '#111827',
    textAlign: 'center',
    flex: 1,
  },
  dotsContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '8px',
    marginTop: '24px',
    width: '100%',
  },
  dot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#D1D5DB',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    padding: 0,
    border: 'none',
    outline: 'none',
    '&:hover': {
      backgroundColor: '#9CA3AF',
    }
  },
  activeDot: {
    width: '24px',
    borderRadius: '4px',
    backgroundColor: '#6366F1',
  },
}));

function safeNumber(val) {
  if (val == null) return 0;
  const n = Number(val);
  return Number.isFinite(n) ? n : 0;
}

function safeFixed(val, decimals) {
  return safeNumber(val).toFixed(decimals).replace('.', ',');
}

const getApdexStatusFromColor = (color, t) => {
  if (color === 'success') {
    return {
      label: t('home.apdex.status.satisfied', { defaultValue: 'Satisfecho' }),
      color: '#34D399',
      bgColor: '#ECFDF5',
      textColor: '#047857',
      subText: t('home.apdex.status.satisfiedDesc', { defaultValue: 'Rendimiento Satisfecho' }),
      icon: <CheckCircleIcon style={{ color: '#34D399', fontSize: '40px' }} />,
    };
  }
  if (color === 'warning') {
    return {
      label: t('home.apdex.status.tolerable', { defaultValue: 'Tolerable' }),
      color: '#F59E0B',
      bgColor: '#FFFBEB',
      textColor: '#B45309',
      subText: t('home.apdex.status.tolerableDesc', { defaultValue: 'Rendimiento regular/tolerable del paso' }),
      icon: <ErrorIcon style={{ color: '#F59E0B', fontSize: '40px' }} />,
    };
  }
  return {
    label: t('home.apdex.status.frustrating', { defaultValue: 'Frustrante' }),
    color: '#EF4444',
    bgColor: '#FEF2F2',
    textColor: '#B91C1C',
    subText: t('home.apdex.status.frustratingDesc', { defaultValue: 'Rendimiento frustrante del paso' }),
    icon: <CancelIcon style={{ color: '#EF4444', fontSize: '40px' }} />,
  };
};

const SVG_SIZE = 180;
const OUTER_STROKE = 14;
const OUTER_RADIUS = (SVG_SIZE - OUTER_STROKE) / 2;
const OUTER_CIRC = 2 * Math.PI * OUTER_RADIUS;
const INNER_STROKE = 5;
const INNER_RADIUS = OUTER_RADIUS - 12;
const INNER_CIRC = 2 * Math.PI * INNER_RADIUS;

export const ObjectivesApdexPanel = ({ objective }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const objectiveId = objective?.objetivo_id;
  const objectiveName = objective?.nombre || '';

  const [activeIndex, setActiveIndex] = useState(0);

  const today = useMemo(() => new Date(), []);
  const globalStatePeriod = useMemo(
    () => ({ startDate: today, endDate: today, key: 'selection' }),
    [today],
  );

  const { data: stepsApdex, isLoading, isError, error } = useIndicadorApdex({
    objetivoId: objectiveId || '',
    periodo: 'd',
    globalStatePeriod,
  });

  useEffect(() => {
    setActiveIndex(0);
  }, [objectiveId]);

  const apdexSummaries = useMemo(() => {
    if (!Array.isArray(stepsApdex) || stepsApdex.length === 0) return [];

    return stepsApdex.map(step => {
      if (step == null || step.apdex_dato == null) return null;

      const tiempos = step.cantidad_tiempos || {};
      const satisfied = safeNumber(tiempos.satisfactorio);
      const tolerable = safeNumber(tiempos.tolerable);
      const frustrated = safeNumber(tiempos.insatisfactorio);
      const totalSamples = satisfied + tolerable + frustrated;

      return {
        stepName: step.nombre_paso || 'Paso',
        score: safeNumber(step.apdex_dato),
        backendColor: step.color,
        totalSamples,
        satisfiedPct: totalSamples > 0 ? (satisfied / totalSamples) * 100 : 0,
        tolerablePct: totalSamples > 0 ? (tolerable / totalSamples) * 100 : 0,
        frustratedPct: totalSamples > 0 ? (frustrated / totalSamples) * 100 : 0,
      };
    }).filter(Boolean);
  }, [stepsApdex]);

  const apdexSummary = apdexSummaries[activeIndex];

  const maxVisibleDots = 7;
  let startDot = 0;
  let endDot = apdexSummaries.length - 1;

  if (apdexSummaries.length > maxVisibleDots) {
    const half = Math.floor(maxVisibleDots / 2);
    startDot = activeIndex - half;
    endDot = activeIndex + half;

    if (startDot < 0) {
      endDot += Math.abs(startDot);
      startDot = 0;
    }
    if (endDot >= apdexSummaries.length) {
      startDot -= (endDot - apdexSummaries.length + 1);
      endDot = apdexSummaries.length - 1;
    }
  }

  const visibleDots = [];
  for (let i = startDot; i <= endDot; i++) {
    visibleDots.push(i);
  }

  const chartSegments = useMemo(() => {
    if (!apdexSummary) return [];
    const sLen = (safeNumber(apdexSummary.satisfiedPct) / 100) * OUTER_CIRC;
    const tLen = (safeNumber(apdexSummary.tolerablePct) / 100) * OUTER_CIRC;
    const fLen = (safeNumber(apdexSummary.frustratedPct) / 100) * OUTER_CIRC;
    return [
      { key: 'satisfied', color: '#10B981', dashArray: `${sLen} ${OUTER_CIRC}`, dashOffset: 0 },
      { key: 'tolerable', color: '#F59E0B', dashArray: `${tLen} ${OUTER_CIRC}`, dashOffset: -sLen },
      { key: 'frustrated', color: '#EF4444', dashArray: `${fLen} ${OUTER_CIRC}`, dashOffset: -(sLen + tLen) },
    ];
  }, [apdexSummary]);

  const scoreColor = apdexSummary ? getApdexStatusFromColor(apdexSummary.backendColor, t).color : '#9CA3AF';
  const innerDash = apdexSummary
    ? `${safeNumber(apdexSummary.score) * INNER_CIRC} ${INNER_CIRC}`
    : `0 ${INNER_CIRC}`;

  if (!objectiveId) {
    return (
        <Box className={classes.placeholderContainer}>
          <InfoOutlinedIcon style={{ fontSize: 40, color: '#CBD5E1' }} />
          <Typography variant="body2" style={{ color: '#94A3B8', fontWeight: 500 }}>
            {t('home.apdex.selectObjective', { defaultValue: 'Selecciona un objetivo para visualizar su Apdex.' })}
          </Typography>
        </Box>
    );
  }

  if (isLoading) {
    return (
        <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
          <AtCircularProgress />
        </Box>
    );
  }

  const showPlaceholder =
    isError ||
    (typeof error === 'string' && error.toLowerCase().includes('umbral')) ||
    apdexSummaries.length === 0;

  if (showPlaceholder) {
    return (
        <Box className={classes.placeholderContainer}>
          <InfoOutlinedIcon style={{ fontSize: 40, color: '#CBD5E1' }} />
          {objectiveName && (
            <Typography variant="subtitle2" style={{ color: '#1E293B', fontWeight: 700, fontSize: '0.95rem' }}>
              {objectiveName}
            </Typography>
          )}
          <Typography variant="body2" style={{ color: '#64748B', fontWeight: 500, maxWidth: 280 }}>
            {isError && error
              ? error
              : t('home.apdex.noThresholds', { defaultValue: 'Los umbrales de Apdex para este objetivo aún no están configurados.' })}
          </Typography>
          <Link
            to={{
              pathname: "/manageObjectives",
              search: getQueryParams(),
              query: { tab: 4, objetivoId: objectiveId },
            }}
            className={classes.configLink}
          >
            <SettingsIcon fontSize="small" />
            {t('home.apdex.configThresholds', { defaultValue: 'Configurar Umbrales de Apdex' })}
          </Link>
        </Box>
    );
  }

  if (!apdexSummary) return null;

  const current = getApdexStatusFromColor(apdexSummary.backendColor, t);

  const categories = [
    {
      key: 'satisfied',
      label: t('home.apdex.categories.satisfied', { defaultValue: 'Satisfactorio' }),
      value: apdexSummary.satisfiedPct,
      icon: <CheckCircleIcon style={{ color: '#10B981', fontSize: '14px' }} />,
      bgColor: '#F0FDF4',
      textColor: '#15803D',
    },
    {
      key: 'tolerable',
      label: t('home.apdex.categories.tolerable', { defaultValue: 'Tolerable' }),
      value: apdexSummary.tolerablePct,
      icon: <ErrorIcon style={{ color: '#F59E0B', fontSize: '14px' }} />,
      bgColor: '#FFFBEB',
      textColor: '#B45309',
    },
    {
      key: 'frustrated',
      label: t('home.apdex.categories.frustrated', { defaultValue: 'Frustrado' }),
      value: apdexSummary.frustratedPct,
      icon: <CancelIcon style={{ color: '#EF4444', fontSize: '14px' }} />,
      bgColor: '#FEF2F2',
      textColor: '#B91C1C',
    }
  ];

  return (
    <Box display="flex" flexDirection="column" width="100%" flex={1}>
      <Box style={{ width: '100%', marginBottom: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography className={classes.stepName} style={{ maxWidth: '90%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {apdexSummary.stepName}
        </Typography>
      </Box>

      <Box display="flex" alignItems="center" flexWrap="wrap" gridGap="24px" flex={1}>
        <Box className={classes.statusCircle}>
          <svg width={SVG_SIZE} height={SVG_SIZE} style={{ transform: 'rotate(-90deg)', position: 'absolute' }}>
            <circle cx={SVG_SIZE / 2} cy={SVG_SIZE / 2} r={OUTER_RADIUS} fill="none" stroke="#F3F4F6" strokeWidth={OUTER_STROKE} />
            <circle cx={SVG_SIZE / 2} cy={SVG_SIZE / 2} r={INNER_RADIUS} fill="none" stroke="#F3F4F6" strokeWidth={INNER_STROKE} />

            {chartSegments.map((seg) => (
              <circle
                key={seg.key}
                cx={SVG_SIZE / 2}
                cy={SVG_SIZE / 2}
                r={OUTER_RADIUS}
                fill="none"
                stroke={seg.color}
                strokeWidth={OUTER_STROKE}
                strokeDasharray={seg.dashArray}
                strokeDashoffset={seg.dashOffset}
                style={{ transition: 'stroke-dasharray 0.6s ease, stroke-dashoffset 0.6s ease' }}
              />
            ))}

            <circle
              cx={SVG_SIZE / 2}
              cy={SVG_SIZE / 2}
              r={INNER_RADIUS}
              fill="none"
              stroke={scoreColor}
              strokeWidth={INNER_STROKE}
              strokeDasharray={innerDash}
              strokeDashoffset={0}
              strokeLinecap="round"
              style={{ transition: 'stroke-dasharray 0.6s ease' }}
            />
          </svg>
          <Box className={classes.statusIndicator} style={{ zIndex: 1 }}>
            <Typography style={{ color: current.color, fontWeight: 800, fontSize: '2.0rem', lineHeight: 1, fontFamily: '"Inter", "Roboto", sans-serif' }}>
              {safeFixed(apdexSummary.score, 3)}
            </Typography>
          </Box>
        </Box>

        <Box flex={1} minWidth={260}>
          <Typography variant="body2" style={{ color: '#6B7280', marginBottom: '4px' }}>
            {t('home.apdex.score', { defaultValue: 'Calificación Apdex:' })}
          </Typography>

          <Typography variant="body1" style={{ fontWeight: 700, color: '#111827', marginBottom: '24px', fontSize: '1.1rem' }}>
            {current.label} 
            <span style={{ fontWeight: 400, color: '#6B7280', display: 'block', fontSize: '0.95rem', marginTop: '4px' }}>
              {current.subText}
            </span>
          </Typography>

          <Box
            display="flex"
            gridGap="12px"
            width="100%"
          >
            {categories.map((item) => (
              <Box
                key={item.key}
                className={classes.summaryBox}
                style={{
                  backgroundColor: item.bgColor,
                  border: '2px solid transparent',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <Box display="flex" alignItems="center" gridGap="8px" mb={1}>
                  {item.icon}

                  <Typography
                    variant="caption"
                    style={{
                      color: item.textColor,
                      fontWeight: 700,
                      fontSize: '0.8rem',
                    }}
                  >
                    {item.label}
                  </Typography>
                </Box>

                <Typography
                  variant="h5"
                  style={{
                    color: '#111827',
                    fontWeight: 800,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {safeFixed(item.value, 1)}%
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
        <Box
          className={classes.summaryBox}
          style={{
            backgroundColor: '#EFF6FF',
            position: 'absolute',
            top: '24px',
            right: '24px',
            minWidth: 'auto',
            padding: '8px 16px',
            alignItems: 'center',
          }}
        >
          <Typography variant="caption" style={{ color: '#1E40AF', fontWeight: 500 }}>
            {t('home.apdex.totalSamples', { defaultValue: 'Total muestras' })}
          </Typography>
          <Typography variant="h5" style={{ color: '#111827', fontWeight: 700 }}>
            {apdexSummary.totalSamples}
          </Typography>
        </Box>
      </Box>

      <Box display="flex" alignItems="center" justifyContent="center" style={{ marginTop: '16px', position: 'relative', width: '100%', minHeight: '28px' }}>
        {apdexSummaries.length > 1 && (
          <Box className={classes.dotsContainer} style={{ marginTop: 0 }}>
            {startDot > 0 && (
              <div className={classes.dot} style={{ transform: 'scale(0.5)' }} />
            )}
            {visibleDots.map((index) => (
              <button
                key={index}
                onClick={() => setActiveIndex(index)}
                className={`${classes.dot} ${index === activeIndex ? classes.activeDot : ''}`}
                aria-label={`Ir al paso ${index + 1}`}
              />
            ))}
            {endDot < apdexSummaries.length - 1 && (
              <div className={classes.dot} style={{ transform: 'scale(0.5)' }} />
            )}
          </Box>
        )}
        <Link
          to={{
            pathname: "/manageObjectives",
            search: getQueryParams(),
            query: { tab: 4, objetivoId: objectiveId },
          }}
          title={t('home.apdex.configThresholds', { defaultValue: 'Configurar Umbrales de Apdex' })}
          style={{
            position: 'absolute',
            right: '0px',
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            backgroundColor: '#EEF2FF',
            border: '1px solid #E0E7FF',
            color: '#4F46E5',
            textDecoration: 'none',
            transition: 'all 0.2s ease',
          }}
        >
          <SettingsIcon style={{ fontSize: '16px' }} />
        </Link>
      </Box>
    </Box>
  );
};
